import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import useProductCategory from "@/common/hooks/queries/useProductCategory";
import { Boxes } from "lucide-react";
import { Control, Controller, FieldErrors, useFieldArray, UseFormWatch, useWatch } from "react-hook-form";
import { CompanyBranchValidationSchema } from "../../schemas/companyBranch.schema";
import { formatCurrency, formatDecimalUnit } from '@/common/utils/formatters';
import { useClientCompaniesAvailableToAllocateInBranchSummary } from '../../hooks/useClientCompaniesAvailableToAllocateInBranchSummary';
import { ClientCompanyAllowedCategoriesAndProducts, ClientCompanyAllowedCategoryAndProductData } from '../../types/allowedCategoriesAndProducts.type';
import { useMemo } from 'react';
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '../../services/allowedCategoriesAndProductsTransformer';
import Select from '@/common/components/Select';
import { Option } from '@/common/types/option';
import { CreditProductConfig } from './CreditProductConfig';
import useDebounce from "@/common/hooks/useDebounce";
import { filterOptions } from "@/common/constants/filterOptions.constant";
import { FilterOptionOption } from "react-select";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities, unitiesDecimals } from "@/common/constants/unities.constants";

interface CreditProductCategoryConfigProps {
  categoryId: string;
  categoryIndex: number;
  removeCategory: () => void;
  control: Control<CompanyBranchValidationSchema>;
  errors: FieldErrors<CompanyBranchValidationSchema>;
  disabled?: boolean;
  watch: UseFormWatch<CompanyBranchValidationSchema>;
  companyKind?: string
  controlledBy?: CompanyContractConsumptionControlledBy;
}

export const CreditProductCategoryConfig = ({
  categoryId,
  categoryIndex,
  removeCategory,
  control,
  errors,
  disabled,
  watch,
  companyKind,
  controlledBy = CompanyContractConsumptionControlledBy.Currency,
}: CreditProductCategoryConfigProps) => {
  const {
    fields: productGrantedCreditFields,
    append: appendProductGrantedCredit,
    remove: removeProductGrantedCredit,
  } = useFieldArray({
    control,
    name: `product_category_granted_credits_attributes.${categoryIndex}.product_granted_credits_attributes`,
  });

  const isQuantity = controlledBy === CompanyContractConsumptionControlledBy.Quantity;

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    data: allowedCategoriesAndProducts,
    isPending: isAllowedCategoriesAndProductsPending,
  } = useClientCompaniesAvailableToAllocateInBranchSummary<ClientCompanyAllowedCategoriesAndProducts>({
    filters: { search: searchProduct, productCategoryId: categoryId },
  });

  const allowedCategorySummary = useMemo(() => {
    return isAllowedCategoriesAndProductsPending || !allowedCategoriesAndProducts || !allowedCategoriesAndProducts['categories']
      ? undefined
      : allowedCategoriesAndProducts['categories'][categoryId]
  }, [isAllowedCategoriesAndProductsPending]);

  const allowedProductsOptions = ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({ key: 'products', watch, productCategoryIndex: categoryIndex })(allowedCategoriesAndProducts || {});

  const { data: productCategory } = useProductCategory(categoryId);

  const handleAppendProduct = (option: Option & ClientCompanyAllowedCategoryAndProductData) => {
    if (option.value) {
      if (isQuantity) {
        appendProductGrantedCredit({
          product_id: option.value,
          display_name: option.display_name,
          granted_credit: "",
          granted_quantity: "",
        } as unknown as Parameters<typeof appendProductGrantedCredit>[0]);
      } else {
        appendProductGrantedCredit({
          product_id: option.value,
          display_name: option.display_name,
          granted_credit: "",
        });
      }
    }
  };

  const watchGrantedCredit = useWatch({
    control,
    name: 'granted_credit',
  });

  const watchCurrentCategoryGrantedCredit = useWatch({
    control,
    name: `product_category_granted_credits_attributes.${categoryIndex}.granted_credit`,
  });

  const watchCurrentCategoryGrantedQuantity = useWatch({
    control,
    name: `product_category_granted_credits_attributes.${categoryIndex}.granted_quantity` as `product_category_granted_credits_attributes.${number}.granted_credit`,
  });

  const watchProductCategoryGrantedCredits = useWatch({
    control,
    name: 'product_category_granted_credits_attributes',
  });

  const productCategoryGrantedCreditsSum = watchProductCategoryGrantedCredits?.reduce((sum, productCategory, index) => {
    if (index !== categoryIndex) {
      if (isQuantity) {
        const grantedQuantity = unFormatUnknownUnit(productCategory.granted_quantity || '0');
        return sum + grantedQuantity || 0;
      }
      return sum + (unFormatCurrency(productCategory.granted_credit || '') || 0);
    }
    return sum;
  }, 0) || 0;

  const allowedCategoryAvailableTotal = Number(allowedCategorySummary?.available_total) || 0;
  const branchGrantedCredit = watchGrantedCredit ? unFormatCurrency(watchGrantedCredit) : 0
  const branchGrantedCreditLeft = branchGrantedCredit - productCategoryGrantedCreditsSum;

  const availableTotal = allowedCategoryAvailableTotal
    ? Math.min(allowedCategoryAvailableTotal, Math.max(branchGrantedCreditLeft, 0))
    : Math.max(branchGrantedCreditLeft, 0);
  const availableForAllocation = isQuantity
    ? Math.max(
      branchGrantedCredit - (unFormatUnknownUnit(watchCurrentCategoryGrantedQuantity || '0') || 0),
      0,
    )
    : Math.max(
      unFormatCurrency(watchGrantedCredit || '') - unFormatCurrency(watchCurrentCategoryGrantedCredit || ''),
      0,
    );

  const productCategoryUnit = useMemo(() => {
    const firstProductUnit = (productGrantedCreditFields || [])[0]?.product_unit_for_quantity;

    return (productGrantedCreditFields || []).every(p => p.product_unit_for_quantity === firstProductUnit)
      ? firstProductUnit
      : undefined;
  }, [(productGrantedCreditFields || []).length]);

  return (
    <Box className="flex flex-col gap-4">
      <div>
        <Badge
          Icon={Boxes}
          text={productCategory?.display_name || ""}
          onDelete={() => removeCategory()}
          className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
          overwriteClassName
          deleteClassName="bg-black p-2"
          contentClassName="px-3 py-2"
          deleteIconColor="var(--foreground)"
          disabled={disabled}
        />
      </div>

      <div className='flex flex-col gap-1'>
        {isQuantity ? (
          <Controller
            name={`product_category_granted_credits_attributes.${categoryIndex}.granted_quantity`}
            control={control}
            disabled={disabled}
            render={({ field }) => (
              <UnitTextField
                decimals={productCategoryUnit ? unitiesDecimals[productCategoryUnit] : 2}
                max={999_999_999.99}
                unit={productCategoryUnit ? unities[productCategoryUnit] : undefined}
                label="Quantidade total"
                markAsRequired
                errorMessage={errors.product_category_granted_credits_attributes?.[categoryIndex]?.granted_quantity?.message || " "}
                {...field}
              />
            )}
          />
        ) : (
          <Controller
            name={`product_category_granted_credits_attributes.${categoryIndex}.granted_credit`}
            control={control}
            disabled={disabled}
            render={({ field }) => (
              <CurrencyTextField
                label="Valor total"
                markAsRequired
                maxValue={9_999_999.99}
                errorMessage={errors.product_category_granted_credits_attributes?.[categoryIndex]?.granted_credit?.message || " "}
                {...field}
                data-testid={`product_category_granted_credits_attributes_granted_credit_input_${categoryIndex}`}
              />
            )}
          />
        )}

        {allowedCategorySummary && (
          <div className='flex gap-3'>
            <p className='italic text-xs text-white-400'>
              {isQuantity ? 'Quantidade possível: ' : 'Total disponível: '}
              <span data-testid={`product_category_granted_credits_attributes_available_total_${categoryIndex}`}>
                {isQuantity ? formatDecimalUnit(Number(availableTotal), productCategoryUnit) : formatCurrency(availableTotal || 0)}
              </span>
            </p>

            <p className='italic text-xs text-white-400'>
              Disponível para distribuir: {isQuantity ? formatDecimalUnit(Number(availableForAllocation), productCategoryUnit) : formatCurrency(availableForAllocation)}
            </p>

            {companyKind !== 'private_company' && (
              <p className='italic text-xs text-white-400'>
                {isQuantity ? 'Quantidade alocada: ' : 'Total alocado: '}
                {isQuantity
                  ? (allowedCategorySummary?.allocated_quantity
                    ? formatDecimalUnit(Number(allowedCategorySummary?.allocated_quantity), productCategoryUnit)
                    : '--')
                  : (allowedCategorySummary?.allocated_total
                    ? formatCurrency(Number(allowedCategorySummary?.allocated_total) || 0)
                    : '--')
                }
              </p>
            )}

            <p className='italic text-xs text-white-400'>
              {isQuantity ? 'Quantidade distribuída: ' : 'Total distribuído: '}
              {isQuantity
                ? (allowedCategorySummary?.distributed_quantity
                  ? formatDecimalUnit(Number(allowedCategorySummary?.distributed_quantity), productCategoryUnit)
                  : '--')
                : (allowedCategorySummary?.distributed_total
                  ? formatCurrency(Number(allowedCategorySummary?.distributed_total) || 0)
                  : '--')
              }
            </p>
          </div>
        )}
      </div>

      <Select
        name={`search_product_${categoryIndex}`}
        placeholder="Pesquisar produtos"
        options={allowedProductsOptions?.filter((product) => {
          return !productGrantedCreditFields?.some(({ product_id }) => product_id === product.value)
        }) || []}
        onInputChange={setSearchProduct}
        filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
        onChange={(option) => handleAppendProduct(option as Option & ClientCompanyAllowedCategoryAndProductData)}
        value=""
        isDisabled={disabled}
        dataTestid={`searchProduct_selector_${categoryIndex}`}
      />

      {productGrantedCreditFields.map((product, index) => (
        <CreditProductConfig
          key={index}
          product={product}
          unitForQuantity={product.product_unit_for_quantity}
          categoryIndex={categoryIndex}
          productIndex={index}
          allowedProductSummary={allowedCategoriesAndProducts?.['products']?.[product.product_id]}
          control={control}
          errors={errors}
          removeProduct={() => removeProductGrantedCredit(index)}
          disabled={disabled}
          companyKind={companyKind}
          controlledBy={controlledBy}
        />
      ))}
    </Box>
  );
};
